import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FavoriteIcon from '@mui/icons-material/Favorite';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useMyList } from '../../hooks/useMyList';
import {
  PILL_MIN_HEIGHT, PILL_RADIUS, pillBg, pillBgHover,
} from './headerPill';

interface MyListButtonProps {
  /** When true, render as a full-width list row (inside the mobile menu). */
  forceLabel?: boolean;
  /** Fired on click (e.g. to close the mobile menu before navigating). */
  onNavigate?: () => void;
}

/** Header entry point to the My List page, with an inline saved-school count. */
const MyListButton = ({ forceLabel, onNavigate }: MyListButtonProps = {}) => {
  const { t } = useTranslation();
  const items = useMyList();
  const count = items.length;

  // Label + muted "(n)" count, grouped so both share responsive visibility.
  const label = (
    <>
      {t('finder.mylist_title')}
      {count > 0 && (
        <Box component="span" sx={{ color: 'text.secondary', ml: 0.75, fontWeight: 500 }}>
          (
          {count}
          )
        </Box>
      )}
    </>
  );

  const icon = (
    <FavoriteIcon color="error" sx={{ fontSize: forceLabel ? 20 : 18 }} />
  );

  if (forceLabel) {
    return (
      <Button
        component={RouterLink}
        to="/my-list"
        onClick={onNavigate}
        aria-label={t('finder.mylist_title')}
        startIcon={icon}
        sx={{
          justifyContent: 'flex-start',
          borderRadius: 0,
          px: 2,
          minHeight: 52,
          color: 'text.primary',
          fontSize: '0.875rem',
          fontWeight: 500,
          textTransform: 'none',
          '& .MuiButton-startIcon': { ml: 0, mr: 1.5 },
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        {label}
      </Button>
    );
  }

  return (
    <Button
      component={RouterLink}
      to="/my-list"
      onClick={onNavigate}
      aria-label={t('finder.mylist_title')}
      startIcon={icon}
      sx={{
        minHeight: PILL_MIN_HEIGHT,
        borderRadius: PILL_RADIUS,
        px: 2,
        color: 'text.primary',
        bgcolor: pillBg,
        fontSize: '0.875rem',
        fontWeight: 500,
        textTransform: 'none',
        whiteSpace: 'nowrap',
        '&:hover': { bgcolor: pillBgHover },
        '& .MuiButton-startIcon': { mr: { xs: 0, sm: 1 } },
      }}
    >
      <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
        {label}
      </Box>
    </Button>
  );
};

export default MyListButton;
